import { useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import api from '../api/api';
import { EVEN_WEB_URL } from '../constants/config';
import { dataActions } from '../store/data';

// Deep link paths from the Even Partner App Integration Guide (Section 6B).
export const EVEN_PATHS = {
  home: '/',
  consultDoctor: '/even_doctor_booking?d=%7B%222%22%3A2%2C%223%22%3A1%7D',
  buyMedicines: '/even_medicine_shopping_flow',
  bookLabTests: '/even_test_shopping_flow',
};

// Even features reachable in-app at /even/:feature. `title` is shown in the page header.
export const EVEN_FEATURES: Record<string, { path: string; title: string }> = {
  'buy-medicines': { path: EVEN_PATHS.buyMedicines, title: 'Medicines' },
  'lab-tests': { path: EVEN_PATHS.bookLabTests, title: 'Lab Tests' },
  'consult-doctor': { path: EVEN_PATHS.consultDoctor, title: 'Consult Doctor' },
};

export const getEvenFeature = (pathname: string) => EVEN_FEATURES[pathname.split('/')[2] || ''];

const getEvenAuthToken = (response: any): string | null => {
  if (typeof response === 'string') return response || null;
  if (typeof response?.data === 'string') return response.data || null;

  return response?.token || response?.auth_token || response?.data?.token || response?.data?.auth_token || null;
};

// Mints a fresh Even session token via our backend (Integration Guide, Section 3A).
export const requestEvenAuthToken = async (phone?: string, signal?: AbortSignal): Promise<string> => {
  if (!phone) throw new Error('Your registered phone number could not be found.');

  const token = getEvenAuthToken(await api.even.getAuthToken({ mobile: phone }, signal));
  if (!token) throw new Error('Even authentication token was not returned');

  return token;
};

export const buildEvenLoginUrl = (
  token: string,
  memberId?: string | number | null,
  fullName?: string,
  path: string = EVEN_PATHS.home
) => {
  const url = new URL(path, EVEN_WEB_URL);

  if (url.protocol !== 'https:') throw new Error('Even Web URL must use HTTPS');

  // Appended by hand so the deep link's own pre-encoded query (e.g. `d=`) is kept exactly as Even provides it.
  const params = new URLSearchParams({ even_auth_token: token });
  if (memberId) params.set('member_id', String(memberId));
  if (fullName) params.set('full_name', fullName);

  return `${url.origin}${url.pathname}${url.search ? `${url.search}&` : '?'}${params.toString()}`;
};

export type EvenAuthStatus = 'idle' | 'checking' | 'available' | 'unavailable';

// Even tokens are valid for ~1 hour; stop reusing a cached one a little before that so it can't expire mid-load.
const EVEN_TOKEN_MAX_AGE_MS = 50 * 60 * 1000;

/** The prefetched Even token for this phone, if it is still fresh enough to open Even with. */
export const selectCachedEvenToken = (state: any, phone?: string): string | null => {
  const evenAuth = state?.data?.evenAuth;
  if (!phone || evenAuth?.phone !== phone || !evenAuth?.token || !evenAuth?.issuedAt) return null;

  return Date.now() - evenAuth.issuedAt < EVEN_TOKEN_MAX_AGE_MS ? evenAuth.token : null;
};

/**
 * Fetches an Even token for this user once per session and keeps it in the store, so Even entry points can be
 * hidden when the backend can't issue one, and the Even page can open with the cached token instead of waiting
 * on a fresh call. Navigating back doesn't re-run the check, so the Even links don't pop in late.
 */
export const useEvenAuthStatus = ({ enabled = false }: { enabled?: boolean } = {}): EvenAuthStatus => {
  const dispatch = useDispatch(),
    store = useStore(),
    phone = useSelector((state: any) => state.auth?.phone),
    evenAuth = useSelector((state: any) => state.data?.evenAuth);

  useEffect(() => {
    if (!enabled || !phone) return;

    // Read the store directly so two components mounting together don't both start a check.
    const current = (store.getState() as any).data?.evenAuth;
    if (current?.phone === phone && current?.status !== 'idle') return;

    dispatch(dataActions.setEvenAuth({ phone, status: 'checking' }));
    // Deliberately not aborted on unmount: the result is cached for the next screen that needs it.
    requestEvenAuthToken(phone)
      .then((token) => dispatch(dataActions.setEvenAuth({ phone, status: 'available', token, issuedAt: Date.now() })))
      .catch(() => dispatch(dataActions.setEvenAuth({ phone, status: 'unavailable' })));
  }, [enabled, phone, dispatch, store]);

  return evenAuth?.phone === phone ? evenAuth.status : 'idle';
};
